import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import { checkNicknameAvailability, getMyProfile, requestProfileImageUploadForEdit, updateMyProfile } from "../api/users";
import { resolveProfileImageUrl } from "../config/media";
import { HomeFooter } from "../components/home/HomeFooter";
import { HomeHeader, invalidateUserSummaryCache } from "../components/home/HomeHeader";
import { ChevronIcon, SelectedCheckIcon } from "../components/shared/Icons";
import { HEADER_SEARCH_KEYWORD_SUGGESTIONS } from "../config/searchSuggestions";
import { loadAuthSession } from "../data/authSession";
import { KOREA_REGIONS } from "../data/koreaRegions";
import {
  formatProfileGenresDisplay,
  formatProfileInstrumentsDisplay,
  PROFILE_EDIT_DEFAULT_PHOTO,
  PROFILE_EDIT_DEFAULTS,
  type ProfileEditGender,
} from "../data/profileEdit";
import {
  ProfileGenreInstrumentModal,
  type ProfileGenreInstrumentTab,
} from "../components/profile/ProfileGenreInstrumentModal";
import "../styles/profile-edit.css";

/** Figma 6415:78154 — 닉네임 사용가능 체크 (Blue #2C80FF) */
function NicknameAvailableGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="profile-edit__nick-available-icon"
      fill="none"
      height="14"
      viewBox="0 0 14 14"
      width="14"
    >
      <path
        d="M3 7.15L5.65 9.8L11 4.45"
        stroke="#2C80FF"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

/** Figma 6415:78014 — 프로필 카메라 버튼 */
function ProfileEditCameraGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="profile-edit__camera-glyph"
      fill="none"
      viewBox="0 0 22 18"
      width="22"
      height="18"
    >
      <path
        d="M2.75 4.5h2.2L6.6 2.25h8.8l1.65 2.25h2.2c.76 0 1.375.62 1.375 1.375v9.9c0 .76-.62 1.375-1.375 1.375H2.75c-.76 0-1.375-.62-1.375-1.375v-9.9C1.375 5.12 1.99 4.5 2.75 4.5z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="11" cy="10.35" fill="currentColor" r="2.75" />
    </svg>
  );
}

export function ProfileEditPage() {
  const navigate = useNavigate();
  const isAuthenticated = Boolean(loadAuthSession());
  const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState("");
  const headerSearchRef = useRef<HTMLDivElement | null>(null);

  const initialNickname = PROFILE_EDIT_DEFAULTS.nickname;
  /** 서버(또는 최초 로드) 기준 닉네임 — 수정 여부 판단용 */
  const [savedNickname, setSavedNickname] = useState(initialNickname);
  const [nickname, setNickname] = useState(initialNickname);
  /** 마지막으로 중복확인 통과한 문자열(초기는 저장값과 동일 = 별도 확인 없이 제출 가능) */
  const [checkedNickname, setCheckedNickname] = useState(initialNickname);
  const [gender, setGender] = useState<ProfileEditGender>(
    PROFILE_EDIT_DEFAULTS.gender,
  );
  const [region, setRegion] = useState(PROFILE_EDIT_DEFAULTS.region);
  const [genres, setGenres] = useState<string[]>(PROFILE_EDIT_DEFAULTS.genres);
  const [instruments, setInstruments] = useState<string[]>(
    PROFILE_EDIT_DEFAULTS.instruments,
  );
  const [giModalOpen, setGiModalOpen] = useState(false);
  const [giModalTab, setGiModalTab] =
    useState<ProfileGenreInstrumentTab>("genre");
  const [bio, setBio] = useState(PROFILE_EDIT_DEFAULTS.bio);
  const [photoUrl, setPhotoUrl] = useState(PROFILE_EDIT_DEFAULT_PHOTO);
  const [regionOpen, setRegionOpen] = useState(false);
  const regionRef = useRef<HTMLDivElement>(null);
  /** Figma 6200:9477 — 변경 전에는 수정완료 비활성(회색) */
  const [formDirty, setFormDirty] = useState(false);
  /** 서버에서 받은 원본 gender 문자열 — PREFER_NOT_TO_SAY 보존용 */
  const [serverGender, setServerGender] = useState<string | null>(null);
  const [, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const savedTrim = savedNickname.trim();
  const nickTrim = nickname.trim();

  const isSameAsSaved = nickTrim.length > 0 && nickTrim === savedTrim;
  const isSameAsChecked =
    nickTrim.length > 0 && nickTrim === checkedNickname.trim();
  /** 닉네임을 건드리지 않았거나(저장값과 동일), 새 값으로 중복확인까지 끝난 경우만 제출 가능 */
  const nickOkForSubmit =
    nickTrim.length > 0 && (isSameAsSaved || isSameAsChecked);
  /** 저장값과 다르고 아직 확인 안 된 입력 → 중복확인 표시 */
  const showDuplicateBtn =
    nickTrim.length > 0 && !isSameAsSaved && !isSameAsChecked;
  /** Figma 6415:78154 — 저장 닉과 다른 새 닉만 「사용가능」(6200 기본 화면에는 없음) */
  const showAvailable =
    nickTrim.length > 0 && !isSameAsSaved && isSameAsChecked;
  const nickRowPlain = !showDuplicateBtn && !showAvailable;

  const canSubmit = nickOkForSubmit && formDirty;

  const closeRegion = useCallback(() => setRegionOpen(false), []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!headerSearchRef.current?.contains(event.target as Node)) {
        setHeaderSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const filteredSuggestions = HEADER_SEARCH_KEYWORD_SUGGESTIONS.filter((item) =>
    item.toLowerCase().includes(headerSearchQuery.toLowerCase()),
  );

  const onHeaderSearchSubmit = (value: string) => {
    const q = value.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  useEffect(() => {
    if (!regionOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = regionRef.current;
      if (!el || el.contains(e.target as Node)) return;
      closeRegion();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRegion();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [regionOpen, closeRegion]);

  const onPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormDirty(true);
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPhotoUrl((prev) => {
      if (prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return url;
    });
  };

  useEffect(() => {
    return () => {
      if (photoUrl.startsWith("blob:")) URL.revokeObjectURL(photoUrl);
    };
  }, [photoUrl]);

  useEffect(() => {
    let cancelled = false;
    getMyProfile()
      .then((profile) => {
        if (cancelled) return;
        setNickname(profile.nickname ?? '');
        setSavedNickname(profile.nickname ?? '');
        setCheckedNickname(profile.nickname ?? '');
        setBio(profile.bio ?? '');
        setPhotoUrl(
          resolveProfileImageUrl(profile.profileImageRef, profile.profileImageUrl)
            ?? PROFILE_EDIT_DEFAULT_PHOTO
        );
        if (profile.gender) {
          setServerGender(profile.gender);
          const genderMap: Record<string, ProfileEditGender> = { MALE: 'male', FEMALE: 'female' };
          if (genderMap[profile.gender]) {
            setGender(genderMap[profile.gender]);
          }
        }
        if (profile.regionCode) {
          setRegion(profile.regionCode);
        }
        if (profile.genres) {
          setGenres(profile.genres.split(',').map(s => s.trim()).filter(Boolean));
        }
        if (profile.instruments) {
          setInstruments(profile.instruments.split(',').map(s => s.trim()).filter(Boolean));
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const onDuplicateCheck = async () => {
    if (!nickTrim) return;
    try {
      const result = await checkNicknameAvailability(nickTrim);
      if (result.available) {
        setCheckedNickname(nickTrim);
      } else {
        setSubmitError('이미 사용 중인 닉네임입니다.');
      }
    } catch {
      setSubmitError('닉네임 확인에 실패했습니다.');
    }
  };

  return (
    <main className="profile-edit-page">
      <HomeHeader
        authenticated={isAuthenticated}
        filteredSuggestions={filteredSuggestions}
        onGuestCta={() => navigate("/login")}
        onSearchChange={(value) => {
          setHeaderSearchQuery(value);
          setHeaderSearchOpen(Boolean(value.trim()));
        }}
        onSearchClear={() => {
          setHeaderSearchQuery("");
          setHeaderSearchOpen(false);
        }}
        onSearchFocus={() =>
          setHeaderSearchOpen(Boolean(headerSearchQuery.trim()))
        }
        onSearchSubmit={onHeaderSearchSubmit}
        onSuggestionSelect={(value) => {
          setHeaderSearchOpen(false);
          onHeaderSearchSubmit(value);
        }}
        searchOpen={headerSearchOpen}
        searchQuery={headerSearchQuery}
        searchRef={headerSearchRef}
      />

      <div className="profile-edit-page__main">
        <div className="profile-edit">
          <header className="profile-edit__header">
            <button
              type="button"
              className="profile-edit__back"
              onClick={() => navigate(-1)}
              aria-label="뒤로"
            >
              <span className="profile-edit__back-chevron" aria-hidden>
                <ChevronIcon />
              </span>
            </button>
            <h1 className="profile-edit__title">프로필 수정</h1>
          </header>

          <div className="profile-edit__photo-wrap">
            <div className="profile-edit__photo-ring">
              <img className="profile-edit__photo" src={photoUrl} alt="" />
            </div>
            <label className="profile-edit__camera">
              <input
                type="file"
                accept="image/*"
                className="profile-edit__camera-input"
                onChange={onPhotoChange}
              />
              <span className="profile-edit__camera-btn" aria-hidden>
                <ProfileEditCameraGlyph />
              </span>
            </label>
          </div>

          <div className="profile-edit__fields">
            <div className="profile-edit__field">
              <span className="profile-edit__label">닉네임</span>
              <div
                className={`profile-edit__nick-row${nickRowPlain ? " profile-edit__nick-row--plain" : ""}`}
              >
                <input
                  className="profile-edit__nick-input"
                  value={nickname}
                  onChange={(e) => {
                    setFormDirty(true);
                    setNickname(e.target.value);
                  }}
                  maxLength={20}
                  placeholder="닉네임"
                  aria-invalid={showDuplicateBtn}
                />
                {showDuplicateBtn ? (
                  <button
                    type="button"
                    className="profile-edit__nick-dup"
                    onClick={onDuplicateCheck}
                  >
                    중복확인
                  </button>
                ) : null}
                {showAvailable ? (
                  <span className="profile-edit__nick-available">
                    <NicknameAvailableGlyph />
                    사용가능
                  </span>
                ) : null}
              </div>
            </div>

            <div className="profile-edit__field">
              <span className="profile-edit__label">성별</span>
              <div
                className="profile-edit__gender-row"
                role="group"
                aria-label="성별"
              >
                <button
                  type="button"
                  className={`profile-edit__gender-pill${gender === "male" ? " profile-edit__gender-pill--active" : ""}`}
                  onClick={() => {
                    setFormDirty(true);
                    setGender("male");
                  }}
                >
                  {gender === "male" ? (
                    <span
                      className="profile-edit__gender-corner-check"
                      aria-hidden
                    >
                      <SelectedCheckIcon />
                    </span>
                  ) : null}
                  남자
                </button>
                <button
                  type="button"
                  className={`profile-edit__gender-pill${gender === "female" ? " profile-edit__gender-pill--active" : ""}`}
                  onClick={() => {
                    setFormDirty(true);
                    setGender("female");
                  }}
                >
                  {gender === "female" ? (
                    <span
                      className="profile-edit__gender-corner-check"
                      aria-hidden
                    >
                      <SelectedCheckIcon />
                    </span>
                  ) : null}
                  여자
                </button>
              </div>
            </div>

            <div className="profile-edit__field" ref={regionRef}>
              <span className="profile-edit__label">사는 지역</span>
              <button
                type="button"
                className={`profile-edit__select${regionOpen ? " profile-edit__select--open" : ""}`}
                onClick={() => setRegionOpen((o) => !o)}
                aria-expanded={regionOpen}
                aria-haspopup="listbox"
              >
                <span className="profile-edit__select-value">{region}</span>
                <span
                  className="profile-edit__select-chevron profile-edit__select-chevron--sm"
                  aria-hidden
                >
                  <ChevronIcon />
                </span>
              </button>
              {regionOpen ? (
                <ul className="profile-edit__select-menu" role="listbox">
                  {KOREA_REGIONS.map((r) => (
                    <li key={r} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={region === r}
                        className={`profile-edit__select-option${region === r ? " profile-edit__select-option--selected" : ""}`}
                        onClick={() => {
                          setFormDirty(true);
                          setRegion(r);
                          setRegionOpen(false);
                        }}
                      >
                        <span
                          className="profile-edit__select-option-check"
                          aria-hidden
                        >
                          {region === r ? <SelectedCheckIcon /> : null}
                        </span>
                        {r}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="profile-edit__field">
              <span className="profile-edit__label">선호하는 장르/악기</span>
              <div className="profile-edit__gi-row">
                <button
                  type="button"
                  className="profile-edit__select profile-edit__select--trigger"
                  onClick={() => {
                    setGiModalTab("genre");
                    setGiModalOpen(true);
                  }}
                >
                  <span
                    className={`profile-edit__select-value${formatProfileGenresDisplay(genres) ? "" : " profile-edit__select-value--placeholder"}`}
                  >
                    {formatProfileGenresDisplay(genres) || "장르 선택"}
                  </span>
                  <span
                    className="profile-edit__select-chevron profile-edit__select-chevron--sm"
                    aria-hidden
                  >
                    <ChevronIcon />
                  </span>
                </button>
                <button
                  type="button"
                  className="profile-edit__select profile-edit__select--trigger"
                  onClick={() => {
                    setGiModalTab("instrument");
                    setGiModalOpen(true);
                  }}
                >
                  <span
                    className={`profile-edit__select-value${formatProfileInstrumentsDisplay(instruments) ? "" : " profile-edit__select-value--placeholder"}`}
                  >
                    {formatProfileInstrumentsDisplay(instruments) ||
                      "악기 선택"}
                  </span>
                  <span
                    className="profile-edit__select-chevron profile-edit__select-chevron--sm"
                    aria-hidden
                  >
                    <ChevronIcon />
                  </span>
                </button>
              </div>
            </div>

            <label className="profile-edit__field profile-edit__field--bio">
              <span className="profile-edit__label">소개 (선택)</span>
              <textarea
                className="profile-edit__textarea"
                value={bio}
                onChange={(e) => {
                  setFormDirty(true);
                  setBio(e.target.value);
                }}
                maxLength={200}
                rows={5}
                placeholder="소개를 입력해 주세요"
              />
              <span className="profile-edit__bio-count">{bio.length}/200</span>
            </label>
          </div>

          <div className="profile-edit__submit-wrap">
            {submitError ? <p style={{ color: '#e74c3c', fontSize: '13px', textAlign: 'center', marginBottom: '8px' }}>{submitError}</p> : null}
            <button
              type="button"
              className={`profile-edit__submit${canSubmit && !submitLoading ? " profile-edit__submit--active" : ""}`}
              disabled={!canSubmit || submitLoading}
              onClick={async () => {
                if (!canSubmit || submitLoading) return;
                setSubmitLoading(true);
                setSubmitError('');
                try {
                  let imageRef: string | undefined;
                  let imageUrl: string | undefined;
                  let ownershipTicket: string | undefined;

                  // Upload new image if selected — grant → S3 PUT → commit (eager)
                  if (selectedFile) {
                    const { prepareImageForUpload, putAndCommit } = await import('../api/media');
                    const uploadFile = await prepareImageForUpload(selectedFile);
                    const grant = await requestProfileImageUploadForEdit(
                      uploadFile.name,
                      uploadFile.type,
                      uploadFile.size,
                    );
                    await putAndCommit({
                      mediaId: grant.profileImageRef,
                      uploadUrl: grant.uploadUrl,
                      uploadHeaders: grant.uploadHeaders,
                      ownershipTicket: grant.ownershipTicket,
                      file: uploadFile,
                    });
                    imageRef = grant.profileImageRef;
                    imageUrl = grant.publicUrl;
                    ownershipTicket = grant.ownershipTicket;
                  }

                  const mappedGender = gender === 'male' ? 'MALE' : 'FEMALE';
                  await updateMyProfile({
                    nickname: nickTrim,
                    bio: bio || undefined,
                    gender: mappedGender !== serverGender ? mappedGender : undefined,
                    regionCode: region,
                    genres: genres.join(',') || undefined,
                    instruments: instruments.join(',') || undefined,
                    profileImageRef: imageRef,
                    profileImageUrl: imageRef ? imageUrl : undefined,
                    ownershipTicket: imageRef ? ownershipTicket : undefined,
                  });
                  // Force header to refetch latest profile on next render.
                  invalidateUserSummaryCache();
                  setSuccessOpen(true);
                } catch (err) {
                  if (err instanceof ApiError) {
                    setSubmitError(err.message);
                  } else {
                    setSubmitError('프로필 수정에 실패했습니다.');
                  }
                } finally {
                  setSubmitLoading(false);
                }
              }}
            >
              {submitLoading ? '저장 중...' : '수정완료'}
            </button>
          </div>

          <ProfileGenreInstrumentModal
            initialGenres={genres}
            initialInstruments={instruments}
            initialTab={giModalTab}
            open={giModalOpen}
            onClose={() => setGiModalOpen(false)}
            onApply={(g, i) => {
              setFormDirty(true);
              setGenres(g);
              setInstruments(i);
            }}
          />
        </div>
      </div>

      {successOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-edit-success-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => {
            setSuccessOpen(false);
            navigate('/');
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '32px 40px',
              minWidth: '320px',
              maxWidth: '90vw',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✓</div>
            <h2 id="profile-edit-success-title" style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0' }}>
              성공했습니다
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px 0' }}>
              프로필이 성공적으로 수정되었습니다.
            </p>
            <button
              type="button"
              onClick={() => {
                setSuccessOpen(false);
                navigate('/');
              }}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '15px',
                fontWeight: 600,
                color: '#fff',
                backgroundColor: 'var(--color-main-yellow, #f5b82e)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              확인
            </button>
          </div>
        </div>
      ) : null}

      <HomeFooter />
    </main>
  );
}
